import { computed, inject, onBeforeUnmount, onMounted, provide, ref, type InjectionKey } from 'vue'
import { useI18n } from '../../i18n'
import { getAdminContext, type PFCAdminContext } from '../../data/wpApi'
import { PLACEHOLDER_GROUPS } from '../model/constants'
import {
  coerceFlowGraphSnapshot,
  toDebugGraph,
  toFlowGraph,
  withExternalMacros,
  withPreviewStartNode,
} from '../model/graphCodec'
import { externalMacroOptions, macroOptions as listMacroOptions } from '../model/macros'
import { nodeSummary as describeNode, nodeTypeLabel as labelType } from '../model/nodeText'
import {
  buildFlowPalette,
  disabledPaletteIds,
  groupPalette,
  PALETTE_SHORTCUTS,
} from '../model/palette'
import { decksListUrl, normalizeFlashcardsRestUrl } from '../model/restUrls'
import { buildFlowSvg } from '../model/svgExport'
import type {
  FlowEditorProps,
  FlowNode,
  FlowSnapshot,
  GroupOption,
  InspectorPanel,
  InteractionTool,
  NodeType,
} from '../model/types'
import { useCanvasPointer } from './useCanvasPointer'
import { useFlowDocument } from './useFlowDocument'
import { useFlowEditing } from './useFlowEditing'
import { useFlowHistory } from './useFlowHistory'
import { useFlowPersistence } from './useFlowPersistence'
import { useFlowPreview } from './useFlowPreview'
import { useFlowSelection } from './useFlowSelection'
import { useFlowTemplates } from './useFlowTemplates'
import { useFlowViewport } from './useFlowViewport'
import { useNodeInspector } from './useNodeInspector'

type Props = FlowEditorProps & { nodeMode: NonNullable<FlowEditorProps['nodeMode']> }

/**
 * Builds the complete editor state for one FlowManagerView instance. Components below
 * the view read it with `useFlowEditorContext()`.
 */
export function useFlowEditor(props: Props) {
  const { t } = useI18n()

  // UI state
  const inspectorPanel = ref<InspectorPanel>('flow')
  const interactionTool = ref<InteractionTool>('marquee')
  const isSpacePanning = ref(false)
  const paletteCollapsed = ref(false)
  const inspectorCollapsed = ref(false)
  const rightRailCollapsed = ref(false)
  const visualDisableShadow = ref(false)
  const groupOptions = ref<GroupOption[]>([...PLACEHOLDER_GROUPS])
  const wpCtx = ref<PFCAdminContext | null>(null)

  // Graph state
  const document = useFlowDocument()
  const { nodes, edges } = document
  const selection = useFlowSelection(document, inspectorPanel)
  const viewport = useFlowViewport()

  let cancelConnection = () => false
  /** Shows a snapshot on the canvas with nothing selected (undo, redo, load). */
  const restore = (snap: FlowSnapshot) => {
    document.replace(snap)
    selection.clearSelection()
    cancelConnection()
  }
  /** Restores a graph that came from storage and reserves its ids. */
  const load = (snap: FlowSnapshot) => {
    restore(snap)
    document.reserveIdsFrom(snap)
  }
  const history = useFlowHistory(document, restore)

  const nodeTypeLabel = (type: NodeType) => labelType(t, type)
  const nodeSummary = (node: FlowNode) => describeNode(t, node, groupOptions.value)

  const palette = computed(() => buildFlowPalette(t))
  const paletteGroups = computed(() => groupPalette(palette.value, t('flow.palette.categoryMisc')))
  const paletteDisabledIds = computed(() => disabledPaletteIds(palette.value, nodes.value))

  const editing = useFlowEditing({
    document,
    selection,
    history,
    viewport,
    groupOptions,
    typeLabel: nodeTypeLabel,
  })
  const pointer = useCanvasPointer({
    document,
    selection,
    history,
    viewport,
    editing,
    interactionTool,
    isSpacePanning,
    palette,
  })
  cancelConnection = pointer.cancelConnection

  /** Delete key / toolbar: first aborts a connection drag, otherwise deletes the selection. */
  const removeSelected = () => {
    if (pointer.cancelConnection()) return
    editing.removeSelected()
  }
  const cutSelected = () => {
    editing.copySelected()
    removeSelected()
  }

  const macroOptions = computed(() =>
    listMacroOptions(
      { nodes: nodes.value, edges: edges.value },
      externalMacroOptions(props.externalMacros ?? []),
    ),
  )
  const inspector = useNodeInspector({ document, selection, history, macroOptions })

  // Derived graphs
  const flowGraph = computed(() => toFlowGraph(nodes.value, edges.value))
  const previewGraph = computed(() =>
    withExternalMacros(
      withPreviewStartNode(flowGraph.value, props.nodeMode),
      props.externalMacros ?? [],
    ),
  )

  const templates = useFlowTemplates({
    document,
    selection,
    history,
    viewport,
    inspectorPanel,
    currentGraph: () => flowGraph.value,
    load,
  })
  const preview = useFlowPreview(wpCtx, t)
  const persistence = useFlowPersistence(wpCtx, groupOptions, load)

  // Save / cancel
  const saveFlow = () => {
    if (history.isDirty.value) history.pushHistory('Save')
    if (props.onSave) {
      props.onSave(flowGraph.value)
      history.markSaved()
      return
    }
    const ctx = wpCtx.value
    if (!ctx) {
      history.markSaved()
      return
    }
    void (async () => {
      if (!(await persistence.saveToWordPress(ctx, flowGraph.value))) return
      history.markSaved()
      window.location.assign(decksListUrl(ctx))
    })()
  }

  const cancelFlow = () => {
    if (props.onCancel) {
      props.onCancel()
      return
    }
    const ctx = wpCtx.value
    if (ctx) {
      window.location.assign(decksListUrl(ctx))
      return
    }
    if (history.lastSavedSnapshot.value) restore(history.lastSavedSnapshot.value)
  }

  // Export
  const copyFlowJson = async () => {
    const text = JSON.stringify(toDebugGraph(nodes.value, edges.value), null, 2)
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
      const area = window.document.createElement('textarea')
      area.value = text
      area.style.position = 'fixed'
      area.style.left = '-9999px'
      window.document.body.appendChild(area)
      area.focus()
      area.select()
      window.document.execCommand('copy')
      window.document.body.removeChild(area)
    } catch {
      // no-op
    }
  }

  const downloadCanvasSvg = () => {
    if (nodes.value.length === 0) return
    const svg = buildFlowSvg(nodes.value, edges.value, {
      typeLabel: (node) => nodeTypeLabel(node.type),
      summary: nodeSummary,
    })
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
    const link = window.document.createElement('a')
    link.href = url
    link.download = 'flowchart.svg'
    link.click()
    URL.revokeObjectURL(url)
  }

  // Keyboard
  const CTRL_SHORTCUTS: Record<string, () => void> = {
    z: history.undo,
    Z: history.redo,
    y: history.redo,
    s: saveFlow,
    c: editing.copySelected,
    x: cutSelected,
    v: () => void editing.pasteSelected(),
    d: editing.duplicateSelected,
    a: () => selection.selectNodes(nodes.value.map((n) => n.id)),
  }

  const onKeydown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null
    const tag = target?.tagName?.toLowerCase() || ''
    if (tag === 'input' || tag === 'textarea' || target?.isContentEditable === true) return

    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()
      removeSelected()
      return
    }

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+Shift+z arrives as 'z' with shiftKey in some layouts, 'Z' in others.
      const key = event.key === 'z' && event.shiftKey ? 'Z' : event.key
      const action = CTRL_SHORTCUTS[key]
      if (action) {
        event.preventDefault()
        action()
      }
      return // never fall through to node-creation shortcuts
    }

    if (event.key === ' ') {
      event.preventDefault()
      isSpacePanning.value = true
      interactionTool.value = 'pan'
      return
    }
    if (event.key === 'Escape' && pointer.cancelConnection()) return
    if (event.key === 's' || event.key === 'S') {
      event.preventDefault()
      interactionTool.value = interactionTool.value === 'pan' ? 'marquee' : 'pan'
      return
    }

    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    }
    const direction = arrows[event.key]
    if (direction) {
      const ids =
        selection.selectedNodeIds.value.length > 0
          ? selection.selectedNodeIds.value
          : selection.selectedNodeId.value
            ? [selection.selectedNodeId.value]
            : []
      if (ids.length > 0) {
        event.preventDefault()
        const step = event.shiftKey ? 20 : 4
        editing.nudgeNodes(ids, direction[0] * step, direction[1] * step)
        return
      }
    }

    // Node creation at the cursor.
    const paletteId = PALETTE_SHORTCUTS[event.key]
    if (!paletteId) return
    event.preventDefault()
    if (paletteId === 'phase' || paletteId === 'group' || paletteId === 'condition') {
      editing.addNodeAt(paletteId, viewport.cursorWorld.value)
    } else {
      const item = palette.value.find((i) => i.id === paletteId)
      if (item) editing.addNodeFromPalette(item, viewport.cursorWorld.value)
    }
  }

  const onKeyup = (event: KeyboardEvent) => {
    if (event.key === ' ') {
      isSpacePanning.value = false
      interactionTool.value = 'marquee'
    }
  }

  // Lifecycle
  const initialise = async () => {
    if (wpCtx.value && !props.initialGraph) {
      await persistence.loadFromWordPress(wpCtx.value)
    } else if (props.initialGraph) {
      if (wpCtx.value) await persistence.loadDeckGroups(wpCtx.value)
      const snap = coerceFlowGraphSnapshot(props.initialGraph)
      if (snap) load(snap)
    }
    history.resetHistory(wpCtx.value ? 'Loaded from WordPress' : 'Initial state')
    if (!persistence.remoteStatus.value) history.markSaved()
  }

  onMounted(() => {
    // Overlay mode (onSave given) never loads from WordPress; it may still preview a deck.
    if (props.previewContext) {
      wpCtx.value = {
        restUrl: props.previewContext.restUrl,
        nonce: props.previewContext.nonce,
        postType: 'pfc_deck',
        postId: props.previewContext.deckId,
      }
    } else if (!props.onSave) {
      wpCtx.value = getAdminContext()
    }
    void initialise()
    window.addEventListener('keydown', onKeydown)
    window.addEventListener('keyup', onKeyup)
  })

  onBeforeUnmount(() => {
    pointer.stopPointer()
    window.removeEventListener('keydown', onKeydown)
    window.removeEventListener('keyup', onKeyup)
  })

  const editor = {
    props,
    t,
    nodeMode: computed(() => props.nodeMode),
    wpCtx,
    previewRestRoot: computed(() =>
      wpCtx.value ? normalizeFlashcardsRestUrl(wpCtx.value.restUrl) : '',
    ),
    ui: {
      inspectorPanel,
      interactionTool,
      paletteCollapsed,
      inspectorCollapsed,
      rightRailCollapsed,
      visualDisableShadow,
    },
    document,
    selection,
    history,
    viewport,
    pointer,
    editing: { ...editing, removeSelected, cutSelected },
    inspector,
    templates,
    preview,
    persistence,
    palette: { items: palette, groups: paletteGroups, disabledIds: paletteDisabledIds },
    groupOptions,
    macroOptions,
    previewGraph,
    nodeTypeLabel,
    nodeSummary,
    saveFlow,
    cancelFlow,
    copyFlowJson,
    downloadCanvasSvg,
  }
  provide(FLOW_EDITOR_KEY, editor)
  return editor
}

export type FlowEditor = ReturnType<typeof useFlowEditor>

const FLOW_EDITOR_KEY: InjectionKey<FlowEditor> = Symbol('flow-editor')

/** The editor of the enclosing FlowManagerView. */
export function useFlowEditorContext(): FlowEditor {
  const editor = inject(FLOW_EDITOR_KEY)
  if (!editor) throw new Error('Flow editor components must be used inside FlowManagerView')
  return editor
}
